import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Pagination from 'reactjs-hooks-pagination';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { Loader } from '../../components/util/Svg';
import { getActiveUsersReport, getInactiveUsersReport } from '../../services/reportService';
import { getDisplayMessage } from '../../helper';

const PAGE_SIZE = 10;

const SORTABLE_COLUMNS = [
    { key: 'fullName', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    { key: 'lastActive', label: 'Last Login Date' },
    { key: 'created', label: 'Registration Date' },
    { key: 'updated', label: 'Updated Date' },
];

const INACTIVE_SORTABLE_COLUMNS = [
    ...SORTABLE_COLUMNS,
    { key: 'subscriptionStatus', label: 'Subscription Status' },
    { key: 'planName', label: 'Plan Name' },
];

const formatDate = (value) => (value ? moment(value).format('DD MMM, YYYY HH:mm') : '-');
const formatDateOnly = (value) => (value ? moment(value).format('DD MMM, YYYY') : '-');

const ActiveUsersReport = ({
    title = 'Active Users Report',
    resultTitle = 'Active Users Report Result',
    mode = 'active',
}) => {
    const isInactive = mode === 'inactive';
    const fetchReportApi = isInactive ? getInactiveUsersReport : getActiveUsersReport;
    const exportFilePrefix = isInactive ? 'Inactive_Users_Report' : 'Active_Users_Report';
    const emptyMessage = isInactive
        ? 'No inactive users found for the selected period'
        : 'No active users found for the selected period';
    const sheetName = isInactive ? 'Inactive Users' : 'Active Users';
    const tableColumns = isInactive ? INACTIVE_SORTABLE_COLUMNS : SORTABLE_COLUMNS;

    const debounceRef = useRef(null);
    const requestIdRef = useRef(0);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [startDateErr, setStartDateErr] = useState('');
    const [endDateErr, setEndDateErr] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [appliedFilters, setAppliedFilters] = useState(null);
    const [list, setList] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loader, setLoader] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState(isInactive ? 'updated' : 'lastActive');
    const [sortOrder, setSortOrder] = useState('desc');

    const totalRecords = pagination ? pagination.totalCount : 0;

    const validateDates = () => {
        let valid = true;
        setStartDateErr('');
        setEndDateErr('');
        setErrorMessage('');

        if (!startDate) {
            setStartDateErr('Start date is required');
            valid = false;
        }
        if (!endDate) {
            setEndDateErr('End date is required');
            valid = false;
        }
        if (startDate && endDate && moment(endDate).isBefore(moment(startDate), 'day')) {
            setEndDateErr('End date cannot be earlier than start date');
            valid = false;
        }
        return valid;
    };

    const buildParams = useCallback((overrides = {}) => {
        const query = new URLSearchParams();
        const nextPage = overrides.page || page;
        const nextLimit = overrides.limit || PAGE_SIZE;
        const nextSearch = overrides.search !== undefined ? overrides.search : search;
        const nextSortBy = overrides.sortBy || sortBy;
        const nextSortOrder = overrides.sortOrder || sortOrder;
        const filters = overrides.filters || appliedFilters;

        query.set('page', String(nextPage));
        query.set('limit', String(nextLimit));
        query.set('sortBy', nextSortBy);
        query.set('sortOrder', nextSortOrder);

        if (filters) {
            query.set('startDate', filters.startDate);
            query.set('endDate', filters.endDate);
        }
        if (nextSearch && nextSearch.trim()) {
            query.set('search', nextSearch.trim());
        }

        return `?${query.toString()}`;
    }, [appliedFilters, page, search, sortBy, sortOrder]);

    const fetchReport = useCallback((overrides = {}) => {
        if (!appliedFilters && !overrides.filters) {
            return;
        }

        const requestId = ++requestIdRef.current;
        setLoader(true);
        setErrorMessage('');

        fetchReportApi(buildParams(overrides)).then((res) => {
            if (requestId !== requestIdRef.current) {
                return;
            }
            setLoader(false);
            if (res.status) {
                setList(res.data.userList || []);
                setPagination({
                    totalCount: res.data.totalCount || 0,
                    currentPage: Number(res.data.currentPage) || 1,
                    pageSize: Number(res.data.pageSize) || PAGE_SIZE,
                    totalPages: res.data.totalPages || 0,
                });
            } else {
                setList([]);
                setPagination(null);
                setErrorMessage(getDisplayMessage(res.data, 'Unable to generate report'));
            }
        });
    }, [appliedFilters, buildParams, fetchReportApi]);

    useEffect(() => {
        if (!appliedFilters) {
            return;
        }
        fetchReport();
    }, [appliedFilters, page, search, sortBy, sortOrder, fetchReport]);

    useEffect(() => {
        if (!appliedFilters) {
            return undefined;
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            setPage(1);
            setSearch(searchInput.trim());
        }, 500);
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [searchInput, appliedFilters]);

    const handleGenerate = (e) => {
        e.preventDefault();
        if (!validateDates()) {
            return;
        }
        setPage(1);
        setSearch(searchInput.trim());
        setAppliedFilters({
            startDate,
            endDate,
        });
    };

    const handleReset = () => {
        setStartDate('');
        setEndDate('');
        setStartDateErr('');
        setEndDateErr('');
        setErrorMessage('');
        setAppliedFilters(null);
        setList([]);
        setPagination(null);
        setPage(1);
        setSearchInput('');
        setSearch('');
        setSortBy(isInactive ? 'updated' : 'lastActive');
        setSortOrder('desc');
    };

    const handleSort = (columnKey) => {
        if (!appliedFilters) {
            return;
        }
        if (sortBy !== columnKey) {
            setSortBy(columnKey);
            setSortOrder('asc');
            setPage(1);
            return;
        }
        if (sortOrder === 'asc') {
            setSortOrder('desc');
            setPage(1);
            return;
        }
        setSortBy(isInactive ? 'updated' : 'lastActive');
        setSortOrder('desc');
        setPage(1);
    };

    const getSortIndicator = (columnKey) => {
        if (sortBy !== columnKey) {
            return ' ↕';
        }
        return sortOrder === 'asc' ? ' ↑' : ' ↓';
    };

    const handleExport = async () => {
        if (!appliedFilters) {
            setErrorMessage('Please generate the report first');
            return;
        }
        if (!totalRecords) {
            setErrorMessage('No records found to export');
            return;
        }

        setExporting(true);
        setErrorMessage('');

        try {
            const EXPORT_PAGE_SIZE = 500;
            const rows = [];
            let exportPage = 1;
            let remaining = totalRecords;

            while (remaining > 0) {
                const res = await fetchReportApi(
                    buildParams({
                        page: exportPage,
                        limit: EXPORT_PAGE_SIZE,
                        search,
                        sortBy,
                        sortOrder,
                        filters: appliedFilters,
                    })
                );

                if (!res.status) {
                    setErrorMessage(getDisplayMessage(res.data, 'Unable to export report'));
                    setExporting(false);
                    return;
                }

                const batch = res.data.userList || [];
                if (!batch.length) {
                    break;
                }

                rows.push(...batch);
                remaining = (res.data.totalCount || totalRecords) - rows.length;
                exportPage += 1;

                if (batch.length < EXPORT_PAGE_SIZE) {
                    break;
                }
            }

            if (!rows.length) {
                setErrorMessage('No records found to export');
                setExporting(false);
                return;
            }

            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet(sheetName);

            const headers = [
                'User ID',
                'Full Name',
                'Email',
                'Mobile Number',
                'Company Name',
                'User Type',
                'Status',
                ...(isInactive ? [
                    'Subscription Status',
                    'Plan Name',
                    'Subscription Start Date',
                    'Subscription End Date',
                ] : []),
                'Registration Date',
                'Last Login Date',
                'Created Date',
                'Updated Date',
                'Verified User',
                'Verified Email',
                'Credits Left',
            ];

            const headerRow = sheet.addRow(headers);
            headerRow.font = { bold: true };
            headerRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE8E8E8' },
                };
            });

            rows.forEach((item) => {
                const row = [
                    String(item.userId || ''),
                    item.fullName || '',
                    item.email || '',
                    item.mobileNumber || '',
                    item.companyName || '',
                    item.userType || '',
                    item.status || '',
                ];

                if (isInactive) {
                    row.push(
                        item.subscriptionStatus || 'No Subscription',
                        item.planName || 'No Plan',
                        item.subscriptionStartDate
                            ? moment(item.subscriptionStartDate).format('YYYY-MM-DD HH:mm')
                            : '-',
                        item.subscriptionEndDate
                            ? moment(item.subscriptionEndDate).format('YYYY-MM-DD HH:mm')
                            : '-'
                    );
                }

                row.push(
                    item.registrationDate ? moment(item.registrationDate).format('YYYY-MM-DD HH:mm') : '',
                    item.lastLoginDate ? moment(item.lastLoginDate).format('YYYY-MM-DD HH:mm') : '',
                    item.createdDate ? moment(item.createdDate).format('YYYY-MM-DD HH:mm') : '',
                    item.updatedDate ? moment(item.updatedDate).format('YYYY-MM-DD HH:mm') : '',
                    item.verifiedUser || '',
                    item.verifiedEmail || '',
                    item.leftPackCredits || 0
                );

                sheet.addRow(row);
            });

            sheet.columns.forEach((column) => {
                let maxLength = 12;
                column.eachCell({ includeEmpty: true }, (cell) => {
                    const value = cell.value ? String(cell.value) : '';
                    maxLength = Math.max(maxLength, value.length + 2);
                });
                column.width = Math.min(maxLength, 40);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const fileDate = moment().format('YYYYMMDD');
            saveAs(blob, `${exportFilePrefix}_${fileDate}.xlsx`);
        } catch (err) {
            console.error(err);
            setErrorMessage('Excel generation failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const periodLabel = useMemo(() => {
        if (!appliedFilters) {
            return '';
        }
        return `${moment(appliedFilters.startDate).format('DD MMM, YYYY')} - ${moment(appliedFilters.endDate).format('DD MMM, YYYY')}`;
    }, [appliedFilters]);

    return (
        <>
            <div className="row">
                <div className="col-md-3 mb-3">
                    <label className="form-label">Start Date</label>
                    <input
                        type="date"
                        className={`form-control rounded-pill ${startDateErr ? 'is-invalid' : ''}`}
                        value={startDate}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            setStartDateErr('');
                            setErrorMessage('');
                        }}
                    />
                    {startDateErr ? <span className="error">{startDateErr}</span> : ''}
                </div>
                <div className="col-md-3 mb-3">
                    <label className="form-label">End Date</label>
                    <input
                        type="date"
                        className={`form-control rounded-pill ${endDateErr ? 'is-invalid' : ''}`}
                        value={endDate}
                        onChange={(e) => {
                            setEndDate(e.target.value);
                            setEndDateErr('');
                            setErrorMessage('');
                        }}
                    />
                    {endDateErr ? <span className="error">{endDateErr}</span> : ''}
                </div>
                <div className="col-md-6 d-flex align-items-end mb-3 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-primary rounded-pill mr-2 mb-2"
                        disabled={loader}
                        onClick={handleGenerate}
                    >
                        {loader ? 'Generating...' : 'Generate Report'}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary rounded-pill mr-2 mb-2"
                        onClick={handleExport}
                        disabled={exporting || !appliedFilters || !totalRecords}
                    >
                        {exporting ? 'Exporting...' : 'Export to Excel'}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary rounded-pill mb-2"
                        onClick={handleReset}
                    >
                        Reset
                    </button>
                </div>
            </div>
            {errorMessage ? <div className="text-danger mb-2">{errorMessage}</div> : ''}

            <div className="mt-2">
                {!appliedFilters ? (
                    <p className="text-muted mb-0">No report generated yet. Select a date range and click Generate Report.</p>
                ) : loader ? (
                    <div className="loader_lg text-center">
                        <Loader />
                    </div>
                ) : (
                    <>
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="mb-1">{resultTitle}</h5>
                                <div className="small text-muted">
                                    Period: {periodLabel} | Total Records: {totalRecords}
                                </div>
                            </div>
                            <div className="mt-2 mt-md-0" style={{ minWidth: 240 }}>
                                <input
                                    type="text"
                                    className="form-control rounded-pill"
                                    placeholder={isInactive
                                        ? 'Search name, email, subscription status...'
                                        : 'Search name, email, company...'}
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table user-table table-hover align-items-center">
                                <thead>
                                    <tr>
                                        <th>S.No.</th>
                                        <th>User ID</th>
                                        {tableColumns.map((col) => (
                                            <th
                                                key={col.key}
                                                style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                onClick={() => handleSort(col.key)}
                                            >
                                                {col.label}
                                                <span style={{ opacity: sortBy === col.key ? 1 : 0.45 }}>
                                                    {getSortIndicator(col.key)}
                                                </span>
                                            </th>
                                        ))}
                                        <th>Mobile Number</th>
                                        <th>Company Name</th>
                                        <th>User Type</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map((item, index) => (
                                        <tr key={item.userId || index}>
                                            <td>{((page - 1) * PAGE_SIZE) + index + 1}</td>
                                            <td className="small">{item.userId}</td>
                                            <td>{item.fullName || '-'}</td>
                                            <td>{item.email || '-'}</td>
                                            <td>{formatDate(item.lastLoginDate)}</td>
                                            <td>{formatDateOnly(item.registrationDate)}</td>
                                            <td>{formatDateOnly(item.updatedDate)}</td>
                                            {isInactive ? (
                                                <>
                                                    <td>{item.subscriptionStatus || 'No Subscription'}</td>
                                                    <td>{item.planName || 'No Plan'}</td>
                                                </>
                                            ) : ''}
                                            <td>{item.mobileNumber || '-'}</td>
                                            <td>{item.companyName || '-'}</td>
                                            <td>{item.userType || '-'}</td>
                                            <td>
                                                <span className={item.status === 'Active' ? 'text-success' : 'text-muted'}>
                                                    {item.status || '-'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {!list.length ? (
                            <h5 className="text-center py-4">{emptyMessage}</h5>
                        ) : ''}

                        {pagination && totalRecords > 0 ? (
                            <Pagination
                                key={`${mode}-users-page-${page}`}
                                totalRecords={totalRecords}
                                pageLimit={PAGE_SIZE}
                                pageRangeDisplayed={5}
                                onChangePage={(nextPage) => setPage(nextPage)}
                            />
                        ) : ''}
                    </>
                )}
            </div>
        </>
    );
};

export default ActiveUsersReport;
